import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, ChevronLeft, ChevronRight, Play, Pause, Flame, ShieldCheck, Award } from 'lucide-react';
import { useCompany } from '../context/ContentContext';

// Four hero product slides using the customer's own product photos
const SLIDES = [
  {
    image: '/uploads/94268e1634304bf88b1e7469e765f4b8.jpg',
    slug: 'shortwave-ir-heater',
    kicker: 'Infrared Excellence',
    title: 'Shortwave IR Heaters',
    headline: 'Faster Heat.\nSmarter Industry.',
    desc: 'Industrial-grade shortwave infrared heaters for rapid, focused heating — ideal for curing, drying & paint booths.',
  },
  {
    image: '/uploads/e9edd22c417344a69256757516c25598.jpg',
    slug: 'ceramic-ir-heaters',
    kicker: 'Premium Range',
    title: 'Ceramic IR Heaters',
    headline: 'Built to Endure.\nEvery Cycle.',
    desc: 'Long-life ceramic infrared emitters engineered for uniform heat distribution and rugged industrial use.',
  },
  {
    image: '/uploads/bda3903233084982ac0d45e932128061.jpg',
    slug: 'water-immersion-heaters',
    kicker: 'Reliable Heating',
    title: 'Water Immersion Heaters',
    headline: 'Heat Anything.\nAnywhere.',
    desc: 'Trusted water immersion heaters with corrosion-resistant sheaths for industrial & domestic tanks.',
  },
  {
    image: '/uploads/6de7da4b178d4cc5a073688ba916a271.jpg',
    slug: 'hot-air-gun-heaters',
    kicker: 'Precision Engineered',
    title: 'Hot Air Gun Heaters',
    headline: 'Pinpoint Heat.\nMaximum Output.',
    desc: 'Compact hot air gun heaters with custom wattage for plastics, shrink-wrap, and assembly lines.',
  },
];

export default function HeroSlider() {
  const company = useCompany();
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setActive((a) => (a + 1) % SLIDES.length), 6000);
    return () => clearInterval(t);
  }, [paused]);

  const go = useCallback((i) => setActive((i + SLIDES.length) % SLIDES.length), []);
  const slide = SLIDES[active];

  return (
    <section className="hero-split">
      {/* Background mesh */}
      <div className="hero-split-bg" aria-hidden>
        <span className="orb orb-1" />
        <span className="orb orb-2" />
        <span className="grid-lines" />
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10 items-center min-h-[clamp(420px,52vw,620px)] py-10 md:py-14">
          {/* LEFT: text */}
          <div className="lg:col-span-6 text-white order-2 lg:order-1">
            <div key={`text-${active}`} className="hero-text-anim">
              <span className="kicker hero-kicker">{slide.kicker}</span>
              <h1 className="hero-h1 mt-4">
                {slide.headline.split('\n').map((line, i) => (
                  <span key={i} className="block">
                    {i === 1 ? <span className="text-[#f7941e]">{line}</span> : line}
                  </span>
                ))}
              </h1>
              <p className="text-white/70 max-w-xl mt-5 text-sm md:text-base leading-relaxed">
                {slide.desc}
              </p>

              <div className="flex flex-wrap gap-3 mt-7">
                <Link to={`/products/${slide.slug}`} className="btn-brand text-sm">
                  View {slide.title} <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${company.phone?.[0] || '+919911565091'}`} className="btn-ghost text-white border-white/40">
                  <Phone className="w-3.5 h-3.5" /> Call Now
                </a>
              </div>

              {/* Trust micro-strip */}
              <div className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 text-[12px] text-white/60 uppercase tracking-[2px]">
                <span className="inline-flex items-center gap-1.5"><Award className="w-3.5 h-3.5 text-[#f7941e]" /> ISO 9001:2015</span>
                <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#f7941e]" /> RoHS · CE</span>
                <span className="inline-flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[#f7941e]" /> Made in Delhi</span>
              </div>
            </div>
          </div>

          {/* RIGHT: visual */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="hero-visual">
              {SLIDES.map((s, i) => (
                <img
                  key={s.image}
                  src={s.image}
                  alt={s.title}
                  className={`hero-visual-img ${i === active ? 'active' : ''}`}
                  loading={i === 0 ? 'eager' : 'lazy'}
                />
              ))}
              {/* Floating spec pill */}
              <div className="hero-spec-pill">
                <span className="hero-spec-dot" />
                <div>
                  <p className="text-[10px] uppercase tracking-[3px] text-white/60">Featured</p>
                  <p className="font-display text-sm uppercase mt-0.5">{slide.title}</p>
                </div>
              </div>
              {/* Number indicator */}
              <div className="hero-counter">
                <span className="text-[#f7941e] text-2xl font-display font-bold">{String(active + 1).padStart(2, '0')}</span>
                <span className="text-white/40 text-sm">/ {String(SLIDES.length).padStart(2, '0')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Controls bar */}
        <div className="hero-controls">
          <div className="hero-dots-wide">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                aria-label={`slide ${i + 1}`}
                onClick={() => go(i)}
                className={i === active ? 'active' : ''}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button aria-label="previous" onClick={() => go(active - 1)} className="hero-arrow"><ChevronLeft className="w-4 h-4" /></button>
            <button aria-label="play pause" onClick={() => setPaused((p) => !p)} className="hero-arrow">
              {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
            <button aria-label="next" onClick={() => go(active + 1)} className="hero-arrow"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
      </div>
    </section>
  );
}
